import React, { createContext, useContext, useState, useEffect } from 'react';
import { message } from 'antd';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('delivery_token');
    const userData = localStorage.getItem('delivery_user');

    if (token && userData) {
      // Proactive token expiry check — tokens expire after 24h
      const tokenCreatedAt = localStorage.getItem('delivery_token_created_at');
      if (tokenCreatedAt) {
        const elapsed = Date.now() - parseInt(tokenCreatedAt, 10);
        const TWENTY_THREE_HOURS = 23 * 60 * 60 * 1000;
        if (elapsed > TWENTY_THREE_HOURS) {
          localStorage.removeItem('delivery_token');
          localStorage.removeItem('delivery_token_created_at');
          localStorage.removeItem('delivery_user');
          setLoading(false);
          return;
        }
      }

      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        setIsAuthenticated(true);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('delivery_token');
        localStorage.removeItem('delivery_token_created_at');
        localStorage.removeItem('delivery_user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (credentials) => {
    try {
      const response = await api.post('/login', credentials);

      const data = response.data.data || response.data;
      if (data.requires_2fa) {
        return { requires_2fa: true, user_id: data.user_id };
      }

      let userData, token;
      if (response.data.data) {
        ({ user: userData, token } = response.data.data);
      } else if (response.data.user && response.data.token) {
        ({ user: userData, token } = response.data);
      } else {
        throw new Error('Invalid response structure');
      }

      if (userData.user_type !== 'delivery') {
        message.error('Access denied. Delivery personnel only.');
        return false;
      }

      localStorage.setItem('delivery_token', token);
      localStorage.setItem('delivery_token_created_at', Date.now().toString());
      localStorage.setItem('delivery_user', JSON.stringify(userData));

      setUser(userData);
      setIsAuthenticated(true);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      message.success('Login successful!');
      return true;
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Login failed';
      message.error(errorMessage);
      return false;
    }
  };

  const verify2fa = async (userId, code) => {
    try {
      const response = await api.post('/verify-2fa-login', { user_id: userId, code });
      const data = response.data.data || response.data;
      const { user: userData, token } = data;

      if (userData.user_type !== 'delivery') {
        message.error('Access denied. Delivery personnel only.');
        return false;
      }

      localStorage.setItem('delivery_token', token);
      localStorage.setItem('delivery_token_created_at', Date.now().toString());
      localStorage.setItem('delivery_user', JSON.stringify(userData));

      setUser(userData);
      setIsAuthenticated(true);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      message.success('Login successful!');
      return true;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Verification failed';
      message.error(errorMessage);
      return false;
    }
  };

  const logout = (sessionExpired = false) => {
    localStorage.removeItem('delivery_token');
    localStorage.removeItem('delivery_token_created_at');
    localStorage.removeItem('delivery_user');
    setUser(null);
    setIsAuthenticated(false);
    delete api.defaults.headers.common['Authorization'];
    if (sessionExpired) {
      window.location.href = '/?session_expired=1';
    } else {
      window.location.href = '/';
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('delivery_user', JSON.stringify(userData));
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    verify2fa,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
